import type {
  DayInterval,
  DayKey,
  OpeningHours,
} from 'features/onboarding/types/onboarding.types';
import { DAYS } from 'features/onboarding/types/onboarding.types';

interface FormatLabels {
  /** Short label per day, e.g. { sunday: "א'", monday: "ב'", ... }. */
  days: Record<DayKey, string>;
  closed: string;
  twentyFour: string;
}

interface DayGroup {
  start: DayKey;
  end: DayKey;
  day: OpeningHours[DayKey];
}

const intervalsEqual = (a: DayInterval[], b: DayInterval[]): boolean => {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i].open !== b[i].open || a[i].close !== b[i].close) return false;
  }
  return true;
};

const sameHours = (
  a: OpeningHours[DayKey],
  b: OpeningHours[DayKey],
): boolean => {
  if (a.enabled !== b.enabled) return false;
  if (!a.enabled) return true;
  if (a.mode !== b.mode) return false;
  if (a.mode === '24h') return true;
  return intervalsEqual(a.intervals, b.intervals);
};

const groupConsecutive = (hours: OpeningHours): DayGroup[] => {
  const groups: DayGroup[] = [];
  for (const day of DAYS) {
    const cur = hours[day];
    const last = groups[groups.length - 1];
    if (last && sameHours(hours[last.end], cur)) {
      last.end = day;
    } else {
      groups.push({ start: day, end: day, day: cur });
    }
  }
  return groups;
};

const formatIntervals = (intervals: DayInterval[]): string =>
  intervals.map((i) => `${i.open}-${i.close}`).join(' / ');

/**
 * Format a structured weekly schedule into a single human-readable line.
 * Groups consecutive days with identical hours so e.g. Sun-Thu 09:00-22:00
 * appears once, not five times. Used both for the Review screen and as the
 * legacy-compatible string sent to the backend / Pipedrive / email.
 *
 * Split shifts inside a day are joined with " / " (e.g. `09:00-14:00 / 17:00-22:00`)
 * so the outer ", " separator between day-groups stays unambiguous.
 */
export const formatOpeningHours = (
  hours: OpeningHours,
  labels: FormatLabels,
): string => {
  const groups = groupConsecutive(hours);
  return groups
    .map((g) => {
      const range =
        g.start === g.end
          ? labels.days[g.start]
          : `${labels.days[g.start]}-${labels.days[g.end]}`;
      if (!g.day.enabled) return `${range} ${labels.closed}`;
      if (g.day.mode === '24h') return `${range} ${labels.twentyFour}`;
      return `${range} ${formatIntervals(g.day.intervals)}`;
    })
    .join(', ');
};

const HE_DAYS: Record<DayKey, string> = {
  sunday: "א'",
  monday: "ב'",
  tuesday: "ג'",
  wednesday: "ד'",
  thursday: "ה'",
  friday: "ו'",
  saturday: "ש'",
};

const EN_DAYS: Record<DayKey, string> = {
  sunday: 'Sun',
  monday: 'Mon',
  tuesday: 'Tue',
  wednesday: 'Wed',
  thursday: 'Thu',
  friday: 'Fri',
  saturday: 'Sat',
};

const AR_DAYS: Record<DayKey, string> = {
  sunday: 'أحد',
  monday: 'إثنين',
  tuesday: 'ثلاثاء',
  wednesday: 'أربعاء',
  thursday: 'خميس',
  friday: 'جمعة',
  saturday: 'سبت',
};

/**
 * Convenience wrapper for places (services, integrations) that don't have a
 * react-i18next `t` handy. Returns a Hebrew-/English-/Arabic-localised string
 * using the canonical short day labels.
 */
export const formatOpeningHoursByLang = (
  hours: OpeningHours,
  lang: string | undefined,
): string => {
  const normalized = (lang ?? 'he').toLowerCase();
  if (normalized.startsWith('en')) {
    return formatOpeningHours(hours, {
      days: EN_DAYS,
      closed: 'closed',
      twentyFour: '24h',
    });
  }
  if (normalized.startsWith('ar')) {
    return formatOpeningHours(hours, {
      days: AR_DAYS,
      closed: 'مغلق',
      twentyFour: '24 ساعة',
    });
  }
  return formatOpeningHours(hours, {
    days: HE_DAYS,
    closed: 'סגור',
    twentyFour: '24 שעות',
  });
};
